import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import { assignRoleToUser, listAssignableRoles, revokeRoleFromUser } from '../../api/userAccounts';
import type { Role, UserAccount, UserRoleAssignment } from '../../api/userAccounts';
import { useAuth } from '../../auth/useAuth';
import { BrandColors } from '../../theme/brand';

/** Khuôn ngày dùng khi gửi lên máy chủ. Trùng với khuôn của kiểu ngày trong .NET. */
const isoDateFormat = 'YYYY-MM-DD';

/** Khuôn ngày hiển thị cho người dùng Việt Nam. */
const displayDateFormat = 'DD/MM/YYYY';

/** Giá trị của biểu mẫu gán vai trò. */
interface AssignFormValues {
  readonly roleId: string;
  readonly validTo: dayjs.Dayjs | null;
  readonly assignmentReason: string;
}

/**
 * Trạng thái hiệu lực của một phép gán.
 *
 * - `effective`: đang có hiệu lực.
 * - `pending`: chưa tới ngày bắt đầu.
 * - `expired`: đã qua ngày kết thúc.
 */
type AssignmentState = 'effective' | 'pending' | 'expired';

/**
 * Suy trạng thái hiệu lực của một phép gán.
 *
 * Phần "có hiệu lực hay không" lấy thẳng cờ máy chủ tính. Chỉ khi phép gán KHÔNG còn hiệu lực mới phải
 * tự phân biệt chưa tới ngày hay đã hết hạn — và hai trường hợp đó khác nhau ở chỗ quan trọng: máy chủ
 * cho gán lại phép gán đã hết hạn, còn phép gán chưa tới ngày thì nó báo là đã gán.
 *
 * @param today Ngày hôm nay, dạng YYYY-MM-DD. Chuỗi ISO so sánh theo thứ tự chữ là đúng thứ tự ngày.
 */
function assignmentState(assignment: UserRoleAssignment, today: string): AssignmentState {
  if (assignment.isEffective) {
    return 'effective';
  }

  return assignment.validFrom > today ? 'pending' : 'expired';
}

/** Đổi ngày dạng YYYY-MM-DD sang dd/MM/yyyy để hiển thị. */
function formatDate(isoDate: string): string {
  return dayjs(isoDate).format(displayDateFormat);
}

/** Thẻ trạng thái hiệu lực của một phép gán. Màu nào cũng đi kèm chữ. */
function renderValidity(assignment: UserRoleAssignment, state: AssignmentState): ReactNode {
  if (state === 'pending') {
    return <Tag>Từ {formatDate(assignment.validFrom)}</Tag>;
  }

  if (state === 'expired') {
    return assignment.validTo === null ? (
      <Tag color="red">Không còn hiệu lực</Tag>
    ) : (
      <Tag color="red">Hết hạn {formatDate(assignment.validTo)}</Tag>
    );
  }

  return assignment.validTo === null ? (
    <Tag color="green">Lâu dài</Tag>
  ) : (
    <Tooltip title="Quyền tạm: sau ngày này vai trò tự hết hiệu lực, không cần ai thu hồi.">
      <Tag color="gold">Đến {formatDate(assignment.validTo)}</Tag>
    </Tooltip>
  );
}

/** Một dòng của bảng vai trò: phép gán kèm trạng thái đã suy sẵn. */
interface AssignmentRow {
  readonly assignment: UserRoleAssignment;
  readonly state: AssignmentState;
}

/** Tham số của hộp quản lý vai trò. */
export interface UserRolesModalProps {
  /** Tài khoản đang quản lý vai trò, hoặc rỗng để đóng hộp. */
  readonly account: UserAccount | null;

  /** Người đang đăng nhập có quyền gán và thu hồi vai trò. */
  readonly canManage: boolean;

  /** Nhận tài khoản sau khi vai trò thay đổi, để danh sách bên ngoài cập nhật theo. */
  readonly onChanged: (updated: UserAccount) => void;

  /** Đóng hộp. */
  readonly onClose: () => void;
}

/**
 * Hộp gán và thu hồi vai trò của một tài khoản.
 *
 * **Vai trò quyết định người dùng làm được những gì**, nên hộp này tách riêng khỏi biểu mẫu sửa thông
 * tin: gán một vai trò là một quyết định phân quyền, còn sửa họ tên thì không. Máy chủ cũng dùng hai
 * quyền khác nhau cho hai việc đó.
 *
 * Bảng vai trò dựng từ `account.roleAssignments` — mọi phép gán, kể cả đã hết hạn, kèm định danh vai
 * trò thật. Trước đây bảng ghép mã vai trò với danh sách vai trò gán được, nên vai trò đã tắt "Gán
 * được" ra định danh rỗng và không thu hồi được từ giao diện.
 *
 * Danh sách vai trò gán được do máy chủ lọc: nó chỉ trả về vai trò còn hoạt động và còn gán được cho
 * người thật. Vai trò dành cho tiến trình tích hợp không xuất hiện — gán nó cho một người là mở một
 * tài khoản có quyền của tiến trình tự động.
 */
export function UserRolesModal({
  account,
  canManage,
  onChanged,
  onClose,
}: UserRolesModalProps): ReactNode {
  const { user } = useAuth();
  const [form] = Form.useForm<AssignFormValues>();

  const [roles, setRoles] = useState<readonly Role[]>([]);
  const [isBusy, setIsBusy] = useState(false);

  const isOpen = account !== null;

  /*
    Tài khoản đang mở là của chính người đang đăng nhập. Máy chủ từ chối tự thu hồi vai trò — đó là
    cách tự tước quyền quản trị của mình — nên giao diện ẩn nút thay vì hiện một nút chắc chắn thất bại.
  */
  const isOwnAccount = account !== null && user?.userId === account.id;

  /*
    Nạp danh sách vai trò khi hộp mở, không nạp lúc dựng trang. Phần lớn lần vào trang quản trị tài
    khoản là để tra cứu hoặc lập tài khoản, không phải để đổi vai trò.
  */
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const controller = new AbortController();

    void (async () => {
      try {
        const loaded = await listAssignableRoles();

        if (!controller.signal.aborted) {
          setRoles(loaded);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          void message.error(
            error instanceof ApiError ? error.message : 'Không nạp được danh sách vai trò.',
          );
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [isOpen]);

  /*
    Phép gán còn hiệu lực hoặc sắp có hiệu lực nằm trên, phép gán đã hết hạn dồn xuống dưới — người
    quản trị mở hộp này trước hết để xem người đó ĐANG có quyền gì.
  */
  const rows = useMemo<readonly AssignmentRow[]>(() => {
    if (account === null) {
      return [];
    }

    const today = dayjs().format(isoDateFormat);

    return account.roleAssignments
      .map((assignment) => ({ assignment, state: assignmentState(assignment, today) }))
      .sort(
        (left, right) =>
          Number(left.state === 'expired') - Number(right.state === 'expired')
          || left.assignment.roleCode.localeCompare(right.assignment.roleCode),
      );
  }, [account]);

  /*
    Ô chọn "Gán thêm vai trò" chỉ loại những vai trò đang có hiệu lực hoặc chưa tới ngày — máy chủ báo
    "đã gán" với hai trường hợp đó. Vai trò đã hết hạn VẪN hiện để gán lại được: máy chủ mở lại phép gán
    cũ thay vì lập phép gán thứ hai.
  */
  const roleOptions = useMemo(() => {
    const stateByRoleId = new Map(rows.map((row) => [row.assignment.roleId, row.state]));

    return roles
      .filter((role) => {
        const state = stateByRoleId.get(role.id);

        return state === undefined || state === 'expired';
      })
      .map((role) => ({
        value: role.id,
        label:
          stateByRoleId.get(role.id) === 'expired'
            ? `${role.code} — ${role.name} (đã hết hạn, gán lại)`
            : `${role.code} — ${role.name}`,
      }));
  }, [roles, rows]);

  async function handleAssign(values: AssignFormValues): Promise<void> {
    if (account === null) {
      return;
    }

    setIsBusy(true);

    try {
      const updated = await assignRoleToUser(account.id, {
        roleId: values.roleId,
        validTo: values.validTo === null ? null : values.validTo.format(isoDateFormat),
        assignmentReason: values.assignmentReason,
      });

      void message.success('Đã gán vai trò.');
      form.resetFields();
      onChanged(updated);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không gán được vai trò.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  async function revoke(target: UserAccount, assignment: UserRoleAssignment): Promise<void> {
    setIsBusy(true);

    try {
      const updated = await revokeRoleFromUser(target.id, assignment.roleId);

      void message.success('Đã thu hồi vai trò.');
      onChanged(updated);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không thu hồi được vai trò.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  function confirmRevoke(row: AssignmentRow): void {
    if (account === null) {
      return;
    }

    const target = account;
    const { assignment, state } = row;

    Modal.confirm({
      title: `Thu hồi vai trò ${assignment.roleCode} của ${target.userName}?`,
      content:
        state === 'expired'
          ? 'Phép gán này đã hết hạn nên người đó hiện không còn quyền của vai trò. Thu hồi chỉ xóa nó '
            + 'khỏi danh sách; muốn cấp lại thì gán lại kèm lý do.'
          : 'Người này mất các quyền của vai trò ngay ở yêu cầu tiếp theo — hệ thống tính lại quyền ở '
            + 'mỗi yêu cầu. Muốn cấp lại thì phải gán lại kèm lý do.',
      okText: 'Thu hồi',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: async () => {
        await revoke(target, assignment);
      },
    });
  }

  const columns: TableColumnsType<AssignmentRow> = [
    {
      title: 'Mã',
      key: 'code',
      width: 220,
      render: (_value: unknown, row: AssignmentRow) => (
        <Typography.Text
          strong
          style={row.state === 'expired' ? { color: BrandColors.inkMuted } : {}}
        >
          {row.assignment.roleCode}
        </Typography.Text>
      ),
    },
    {
      title: 'Tên vai trò',
      key: 'name',
      render: (_value: unknown, row: AssignmentRow) => (
        <span style={row.state === 'expired' ? { color: BrandColors.inkMuted } : {}}>
          {row.assignment.roleName}
        </span>
      ),
    },
    {
      title: 'Hiệu lực',
      key: 'validity',
      width: 170,
      render: (_value: unknown, row: AssignmentRow) => renderValidity(row.assignment, row.state),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_value: unknown, row: AssignmentRow) =>
        canManage && !isOwnAccount ? (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={isBusy}
            onClick={() => {
              confirmRevoke(row);
            }}
          >
            Thu hồi
          </Button>
        ) : null,
    },
  ];

  return (
    <Modal
      title={account === null ? 'Vai trò' : `Vai trò của ${account.userName}`}
      open={isOpen}
      width={720}
      footer={<Button onClick={onClose}>Đóng</Button>}
      onCancel={onClose}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="Vai trò quyết định người dùng làm được những gì"
          description={
            'Thay đổi ở đây có hiệu lực ngay ở yêu cầu tiếp theo của người đó — hệ thống tính lại '
            + 'quyền ở mỗi yêu cầu, không giữ bộ nhớ đệm. Tài khoản không có vai trò nào thì đăng '
            + 'nhập được nhưng không mở được màn hình nào.'
          }
        />

        <Table<AssignmentRow>
          rowKey={(row) => row.assignment.roleId}
          size="small"
          columns={columns}
          dataSource={[...rows]}
          pagination={false}
          locale={{ emptyText: 'Tài khoản chưa được gán vai trò nào.' }}
        />

        {canManage && isOwnAccount && (
          <Typography.Text type="secondary">
            Đây là tài khoản của bạn nên không có nút thu hồi: hệ thống không cho tự bỏ vai trò của
            chính mình. Muốn bỏ vai trò của mình, hãy nhờ một người quản trị khác.
          </Typography.Text>
        )}

        {canManage && (
          <Form<AssignFormValues>
            form={form}
            layout="vertical"
            requiredMark={false}
            disabled={isBusy}
            onFinish={(values) => {
              void handleAssign(values);
            }}
          >
            <Form.Item
              name="roleId"
              label="Gán thêm vai trò"
              extra="Vai trò đã hết hạn vẫn có trong danh sách: gán lại thì phép gán cũ được mở lại."
              rules={[{ required: true, message: 'Chưa chọn vai trò.' }]}
            >
              <Select<string>
                showSearch
                optionFilterProp="label"
                placeholder="Chọn vai trò"
                options={roleOptions}
              />
            </Form.Item>

            <Form.Item
              name="validTo"
              label="Có hiệu lực đến ngày"
              extra="Để trống nếu không giới hạn thời gian. Dùng khi cấp quyền tạm, ví dụ lúc người phụ trách nghỉ phép."
            >
              <DatePicker format={displayDateFormat} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="assignmentReason"
              label="Lý do gán vai trò"
              extra="Đọc lại sau một năm vẫn phải hiểu được vì sao người này có quyền đó."
              rules={[{ required: true, message: 'Chưa ghi lý do gán vai trò.' }]}
            >
              <Input.TextArea rows={2} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={isBusy}>
                Gán vai trò
              </Button>
            </Form.Item>
          </Form>
        )}
      </Space>
    </Modal>
  );
}
